import { Alert, Button, Card, Form, Input, Modal, Select, Space, Tag } from "antd"
import { useState } from "react"
import { siteWorkflowApi } from "../../utils/siteWorkflowApi"
import { inspectionStates } from "../../types/siteWorkflows"
import type { InspectionState } from "../../types/siteWorkflows"
import { useWorkflowResource } from "./useWorkflowResource"
import { WorkflowError, WorkflowHistory } from "./WorkflowCommon"
export function InspectionLifecyclePanel({ inspection }: { inspection: string }) {
  const [open, setOpen] = useState(false),
    [history, setHistory] = useState(false)
  const data = useWorkflowResource(inspection ? `lifecycle/${inspection}` : "", (signal) =>
    siteWorkflowApi.lifecycle(inspection, signal),
  )
  const source = useWorkflowResource(history ? `inspection-source/${inspection}` : "", (signal) =>
    siteWorkflowApi.findings(inspection, signal),
  )
  const [form] = Form.useForm(),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>()
  const save = async () => {
    const fields = await form.validateFields().catch(() => null)
    if (!fields || !data.data) return
    setBusy(true)
    setError(undefined)
    try {
      await siteWorkflowApi.saveLifecycle(data.data, fields.state as InspectionState, fields.reason)
      setOpen(false)
      data.reload()
      source.reload()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }
  const v = data.data
  return (
    <Card size="small" title="Suivi de l’inspection">
      <WorkflowError error={data.error} retry={data.reload} />
      {v && (
        <Space wrap>
          <Tag>{inspectionStates[v.state]}</Tag>
          <Button onClick={() => setOpen(true)}>
            {v.state === "completed" ? "Rouvrir l’inspection" : "Changer l’état / terminer"}
          </Button>
          <Button onClick={() => setHistory(true)}>Historique</Button>
        </Space>
      )}
      {v?.state === "completed" && (
        <Alert
          type="info"
          message="Une photographie des résultats est conservée pour le suivi. Les modifications ultérieures ne changent pas cette photographie ; rouvrez puis terminez l’inspection pour en créer une nouvelle."
        />
      )}
      <Modal
        open={open}
        title="État de l’inspection"
        onCancel={() => setOpen(false)}
        onOk={save}
        confirmLoading={busy}
        maskClosable={false}
        closable={!busy}
        cancelButtonProps={{ disabled: busy }}
        okText="Confirmer"
        cancelText="Fermer"
        destroyOnClose
      >
        <WorkflowError error={error} />
        <Alert
          type="info"
          message="Avant de terminer, attendez que toutes les réponses soient enregistrées. Une photographie des résultats sera conservée. Cette opération ne constitue pas une signature réglementaire."
        />
        <Form
          form={form}
          layout="vertical"
          disabled={busy}
          preserve={false}
          initialValues={{ state: v?.state === "completed" ? "in_progress" : "completed" }}
        >
          <Form.Item name="state" label="Nouvel état" rules={[{ required: true }]}>
            <Select
              options={(v?.state === "completed"
                ? ["in_progress"]
                : ["draft", "in_progress", "completed", "cancelled"]
              ).map((value) => ({ value, label: inspectionStates[value as InspectionState] }))}
            />
          </Form.Item>
          <Form.Item
            name="reason"
            label="Motif"
            rules={[{ required: true, whitespace: true }, { max: 2000 }]}
          >
            <Input.TextArea rows={2} maxLength={2000} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={history}
        title="Historique de l’inspection"
        footer={null}
        onCancel={() => setHistory(false)}
      >
        <WorkflowError error={source.error} retry={source.reload} />
        {source.data?.physicalSiteUuid && (
          <WorkflowHistory site={source.data.physicalSiteUuid} kind="inspection" id={inspection} />
        )}
      </Modal>
    </Card>
  )
}
